// Medical Scores Database - Complete Collection Part 15
// Additional scores from medicalcul.free.fr - S-Z section

import {
  Brain,
  Heart,
  Wind,
  Activity,
  Baby,
  Stethoscope,
  Thermometer,
  Droplets,
  Flame,
  Syringe,
  Pill,
  Bone,
  Scale,
  Clock,
  AlertTriangle,
  Calculator,
  User,
  Beaker,
  Target
} from 'lucide-react';

export const SCORES_PART_15 = {
  // ==================== SCORES S ====================
  salterHarris: {
    name: 'Salter-Harris (Fractures enfant)',
    category: 'Pédiatrie',
    icon: Bone,
    description: 'Classification fractures épiphysaires',
    color: 'orange',
    fields: [
      { id: 'type', label: 'Type Salter-Harris', type: 'select', options: [
        { value: 1, label: 'Type I - Décollement épiphysaire pur' },
        { value: 2, label: 'Type II - Trait métaphysaire + décollement' },
        { value: 3, label: 'Type III - Trait épiphysaire + décollement' },
        { value: 4, label: 'Type IV - Trait métaphyso-épiphysaire' },
        { value: 5, label: 'Type V - Écrasement du cartilage' }
      ]}
    ],
    calculate: (values) => {
      return { score: values.type || 1, max: 5 };
    },
    interpret: (score) => {
      if (score === 1) return { level: 'success', text: 'Salter I', detail: 'Pronostic excellent. Réduction + immobilisation. Pas de trouble croissance.' };
      if (score === 2) return { level: 'success', text: 'Salter II', detail: 'Pronostic bon. Plus fréquent. Réduction orthopédique souvent suffisante.' };
      if (score === 3) return { level: 'warning', text: 'Salter III', detail: 'Réduction anatomique obligatoire. Risque arthrose.' };
      if (score === 4) return { level: 'danger', text: 'Salter IV', detail: 'Chirurgie souvent nécessaire. Risque épiphysiodèse.' };
      return { level: 'danger', text: 'Salter V', detail: 'Souvent méconnu initialement. Haut risque trouble de croissance.' };
    },
    formula: 'Types I-II: bon pronostic. Types III-V: risque de trouble de croissance'
  },

  schwabEngland: {
    name: 'Schwab & England (Parkinson)',
    category: 'Neurologie',
    icon: Brain,
    description: 'Évaluation capacité patient parkinsonien',
    color: 'purple',
    fields: [
      { id: 'percent', label: 'Niveau d\'autonomie', type: 'select', options: [
        { value: 100, label: '100% - Totalement indépendant' },
        { value: 90, label: '90% - Indépendant, légèrement plus lent' },
        { value: 80, label: '80% - Indépendant mais conscient des difficultés' },
        { value: 70, label: '70% - Pas complètement indépendant, 3-4x plus de temps' },
        { value: 60, label: '60% - Dépendance partielle, peut faire la moitié seul' },
        { value: 50, label: '50% - Plus dépendant, aide pour plus de moitié' },
        { value: 40, label: '40% - Très dépendant, fait peu seul' },
        { value: 30, label: '30% - Avec effort, initie peu d\'activités' },
        { value: 20, label: '20% - Ne fait rien seul, peut aider un peu' },
        { value: 10, label: '10% - Totalement dépendant, grabataire' },
        { value: 0, label: '0% - Fonctions végétatives (déglutition, vessie) ne fonctionnent pas' }
      ]}
    ],
    calculate: (values) => {
      return { score: values.percent || 100, max: 100 };
    },
    interpret: (score) => {
      if (score >= 80) return { level: 'success', text: `S&E ${score}%`, detail: 'Autonomie bonne. Maintien qualité de vie.' };
      if (score >= 50) return { level: 'warning', text: `S&E ${score}%`, detail: 'Autonomie partielle. Aide quotidienne nécessaire.' };
      if (score >= 20) return { level: 'danger', text: `S&E ${score}%`, detail: 'Dépendance importante. Aide permanente.' };
      return { level: 'danger', text: `S&E ${score}%`, detail: 'Dépendance totale. Soins continus.' };
    },
    formula: 'Pourcentage d\'autonomie de 100% (normal) à 0% (grabataire)'
  },

  scoff: {
    name: 'SCOFF (Troubles alimentaires)',
    category: 'Psychiatrie',
    icon: Brain,
    description: 'Dépistage troubles du comportement alimentaire',
    color: 'violet',
    fields: [
      { id: 'sick', label: 'Vous faites-vous vomir car vous vous sentez mal d\'avoir trop mangé?', type: 'select', options: [{ value: 0, label: 'Non' }, { value: 1, label: 'Oui' }]},
      { id: 'control', label: 'Avez-vous peur de perdre le contrôle de ce que vous mangez?', type: 'select', options: [{ value: 0, label: 'Non' }, { value: 1, label: 'Oui' }]},
      { id: 'oneStone', label: 'Avez-vous récemment perdu plus de 6 kg en 3 mois?', type: 'select', options: [{ value: 0, label: 'Non' }, { value: 1, label: 'Oui' }]},
      { id: 'fat', label: 'Vous trouvez-vous trop gros(se) alors que les autres vous trouvent trop mince?', type: 'select', options: [{ value: 0, label: 'Non' }, { value: 1, label: 'Oui' }]},
      { id: 'food', label: 'Diriez-vous que la nourriture domine votre vie?', type: 'select', options: [{ value: 0, label: 'Non' }, { value: 1, label: 'Oui' }]}
    ],
    calculate: (values) => {
      let total = 0;
      Object.values(values).forEach(v => { if (v) total += v; });
      return { score: total, max: 5 };
    },
    interpret: (score) => {
      if (score < 2) return { level: 'success', text: `SCOFF ${score}/5`, detail: 'Pas de trouble du comportement alimentaire suspecté.' };
      return { level: 'danger', text: `SCOFF ${score}/5`, detail: 'TCA probable. Évaluation spécialisée recommandée (anorexie, boulimie).' };
    },
    formula: 'SCOFF ≥ 2: sensibilité 97%, spécificité 94% pour TCA'
  },

  stess: {
    name: 'STESS (État de mal épileptique)',
    category: 'Neurologie',
    icon: Brain,
    description: 'Gravité état de mal épileptique',
    color: 'purple',
    fields: [
      { id: 'consciousness', label: 'Niveau de conscience', type: 'select', options: [
        { value: 0, label: 'Alerte ou somnolent' },
        { value: 1, label: 'Stuporeux ou comateux' }
      ]},
      { id: 'type', label: 'Type de crise le plus sévère', type: 'select', options: [
        { value: 0, label: 'Absence, myoclonique, partielle simple' },
        { value: 1, label: 'Partielle complexe' },
        { value: 2, label: 'Généralisée tonico-clonique' }
      ]},
      { id: 'age', label: 'Âge', type: 'select', options: [
        { value: 0, label: '< 65 ans' },
        { value: 2, label: '≥ 65 ans' }
      ]},
      { id: 'history', label: 'Antécédent de crise', type: 'select', options: [
        { value: 0, label: 'Oui' },
        { value: 1, label: 'Non' }
      ]}
    ],
    calculate: (values) => {
      const total = (values.consciousness || 0) + (values.type || 0) + (values.age || 0) + (values.history || 0);
      return { score: total, max: 6 };
    },
    interpret: (score) => {
      if (score <= 2) return { level: 'success', text: `STESS ${score}/6`, detail: 'Faible mortalité (~3%). Bon pronostic.' };
      return { level: 'danger', text: `STESS ${score}/6`, detail: 'Mortalité élevée (~40%). Traitement agressif recommandé.' };
    },
    formula: 'STESS ≤ 2: mortalité ~3%. STESS > 2: mortalité ~40%'
  },

  stopBang: {
    name: 'STOP-BANG (Apnée du sommeil)',
    category: 'ORL',
    icon: Stethoscope,
    description: 'Dépistage syndrome d\'apnée du sommeil',
    color: 'blue',
    fields: [
      { id: 'snoring', label: 'Ronflements forts', type: 'checkbox', points: 1 },
      { id: 'tired', label: 'Souvent fatigué(e), somnolent(e) en journée', type: 'checkbox', points: 1 },
      { id: 'observed', label: 'Quelqu\'un a observé des pauses respiratoires', type: 'checkbox', points: 1 },
      { id: 'pressure', label: 'Hypertension artérielle traitée', type: 'checkbox', points: 1 },
      { id: 'bmi', label: 'BMI > 35', type: 'checkbox', points: 1 },
      { id: 'age', label: 'Âge > 50 ans', type: 'checkbox', points: 1 },
      { id: 'neck', label: 'Tour de cou > 40cm (F) ou > 43cm (H)', type: 'checkbox', points: 1 },
      { id: 'gender', label: 'Sexe masculin', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      if (values.snoring) total += 1;
      if (values.tired) total += 1;
      if (values.observed) total += 1;
      if (values.pressure) total += 1;
      if (values.bmi) total += 1;
      if (values.age) total += 1;
      if (values.neck) total += 1;
      if (values.gender) total += 1;
      return { score: total, max: 8 };
    },
    interpret: (score) => {
      if (score <= 2) return { level: 'success', text: `STOP-BANG ${score}/8`, detail: 'Faible risque de SAOS.' };
      if (score <= 4) return { level: 'warning', text: `STOP-BANG ${score}/8`, detail: 'Risque intermédiaire. Polysomnographie si symptômes.' };
      return { level: 'danger', text: `STOP-BANG ${score}/8`, detail: 'Haut risque de SAOS modéré à sévère. Polysomnographie recommandée.' };
    },
    formula: '0-2: faible risque. 3-4: intermédiaire. ≥ 5: haut risque'
  },

  // ==================== SCORES T-V ====================
  trueloveWitts: {
    name: 'Truelove et Witts (Colite ulcéreuse)',
    category: 'Hépato-Gastro',
    icon: Beaker,
    description: 'Sévérité poussée rectocolite hémorragique',
    color: 'yellow',
    fields: [
      { id: 'stools', label: 'Nombre de selles/jour', type: 'select', options: [
        { value: 1, label: '< 4' },
        { value: 2, label: '4-6' },
        { value: 3, label: '> 6' }
      ]},
      { id: 'blood', label: 'Sang dans les selles', type: 'select', options: [
        { value: 1, label: 'Pas ou peu' },
        { value: 2, label: 'Modéré' },
        { value: 3, label: 'Abondant' }
      ]},
      { id: 'temp', label: 'Température', type: 'select', options: [
        { value: 1, label: '< 37.5°C' },
        { value: 3, label: '≥ 37.5°C' }
      ]},
      { id: 'hr', label: 'Fréquence cardiaque', type: 'select', options: [
        { value: 1, label: '< 90/min' },
        { value: 3, label: '≥ 90/min' }
      ]},
      { id: 'hb', label: 'Hémoglobine', type: 'select', options: [
        { value: 1, label: '> 10.5 g/dL' },
        { value: 3, label: '≤ 10.5 g/dL' }
      ]},
      { id: 'esr', label: 'VS', type: 'select', options: [
        { value: 1, label: '< 30 mm/h' },
        { value: 3, label: '≥ 30 mm/h' }
      ]}
    ],
    calculate: (values) => {
      const total = (values.stools || 1) + (values.blood || 1) + (values.temp || 1) +
                    (values.hr || 1) + (values.hb || 1) + (values.esr || 1);
      return { score: total, max: 18 };
    },
    interpret: (score) => {
      if (score <= 8) return { level: 'success', text: 'Poussée légère', detail: 'Traitement ambulatoire par 5-ASA.' };
      if (score <= 12) return { level: 'warning', text: 'Poussée modérée', detail: 'Corticothérapie orale. Surveillance.' };
      return { level: 'danger', text: 'Poussée sévère', detail: 'Hospitalisation, corticothérapie IV. Risque colectomie.' };
    },
    formula: 'Critères de sévérité: > 6 selles/j + ≥1 critère systémique'
  },

  villalta: {
    name: 'Villalta (Syndrome post-thrombotique)',
    category: 'Cardiologie',
    icon: Heart,
    description: 'Diagnostic syndrome post-thrombotique',
    color: 'red',
    fields: [
      { id: 'pain', label: 'Douleur', type: 'select', options: [{ value: 0, label: 'Absente' }, { value: 1, label: 'Légère' }, { value: 2, label: 'Modérée' }, { value: 3, label: 'Sévère' }]},
      { id: 'cramps', label: 'Crampes', type: 'select', options: [{ value: 0, label: 'Absentes' }, { value: 1, label: 'Légères' }, { value: 2, label: 'Modérées' }, { value: 3, label: 'Sévères' }]},
      { id: 'heaviness', label: 'Lourdeur', type: 'select', options: [{ value: 0, label: 'Absente' }, { value: 1, label: 'Légère' }, { value: 2, label: 'Modérée' }, { value: 3, label: 'Sévère' }]},
      { id: 'paresthesia', label: 'Paresthésies', type: 'select', options: [{ value: 0, label: 'Absentes' }, { value: 1, label: 'Légères' }, { value: 2, label: 'Modérées' }, { value: 3, label: 'Sévères' }]},
      { id: 'pruritus', label: 'Prurit', type: 'select', options: [{ value: 0, label: 'Absent' }, { value: 1, label: 'Léger' }, { value: 2, label: 'Modéré' }, { value: 3, label: 'Sévère' }]},
      { id: 'edema', label: 'Œdème prétibial', type: 'select', options: [{ value: 0, label: 'Absent' }, { value: 1, label: 'Léger' }, { value: 2, label: 'Modéré' }, { value: 3, label: 'Sévère' }]},
      { id: 'induration', label: 'Induration cutanée', type: 'select', options: [{ value: 0, label: 'Absente' }, { value: 1, label: 'Légère' }, { value: 2, label: 'Modérée' }, { value: 3, label: 'Sévère' }]},
      { id: 'hyperpigmentation', label: 'Hyperpigmentation', type: 'select', options: [{ value: 0, label: 'Absente' }, { value: 1, label: 'Légère' }, { value: 2, label: 'Modérée' }, { value: 3, label: 'Sévère' }]},
      { id: 'redness', label: 'Rougeur', type: 'select', options: [{ value: 0, label: 'Absente' }, { value: 1, label: 'Légère' }, { value: 2, label: 'Modérée' }, { value: 3, label: 'Sévère' }]},
      { id: 'veins', label: 'Ectasies veineuses', type: 'select', options: [{ value: 0, label: 'Absentes' }, { value: 1, label: 'Légères' }, { value: 2, label: 'Modérées' }, { value: 3, label: 'Sévères' }]},
      { id: 'pain_calf', label: 'Douleur compression mollet', type: 'select', options: [{ value: 0, label: 'Absente' }, { value: 1, label: 'Légère' }, { value: 2, label: 'Modérée' }, { value: 3, label: 'Sévère' }]},
      { id: 'ulcer', label: 'Ulcère', type: 'checkbox', points: 15 }
    ],
    calculate: (values) => {
      let total = (values.pain || 0) + (values.cramps || 0) + (values.heaviness || 0) +
                  (values.paresthesia || 0) + (values.pruritus || 0) + (values.edema || 0) +
                  (values.induration || 0) + (values.hyperpigmentation || 0) + (values.redness || 0) +
                  (values.veins || 0) + (values.pain_calf || 0);
      if (values.ulcer) total = 15; // Ulcère = automatiquement sévère
      return { score: total, max: 33 };
    },
    interpret: (score) => {
      if (score < 5) return { level: 'success', text: `Villalta ${score}`, detail: 'Pas de syndrome post-thrombotique.' };
      if (score < 10) return { level: 'warning', text: `Villalta ${score}`, detail: 'SPT léger. Compression élastique.' };
      if (score < 15) return { level: 'warning', text: `Villalta ${score}`, detail: 'SPT modéré. Compression classe II-III.' };
      return { level: 'danger', text: `Villalta ${score}`, detail: 'SPT sévère ou ulcère. Prise en charge spécialisée.' };
    },
    formula: '< 5: absent. 5-9: léger. 10-14: modéré. ≥ 15 ou ulcère: sévère'
  },

  vittel: {
    name: 'Vittel (Triage traumatisé)',
    category: 'Urgences',
    icon: AlertTriangle,
    description: 'Critères de triage des patients traumatisés',
    color: 'red',
    fields: [
      { id: 'gcs', label: 'GCS < 13', type: 'checkbox', points: 1 },
      { id: 'sbp', label: 'PAS < 90 mmHg', type: 'checkbox', points: 1 },
      { id: 'spo2', label: 'SpO2 < 90%', type: 'checkbox', points: 1 },
      { id: 'ejection', label: 'Éjection véhicule / passager décédé', type: 'checkbox', points: 1 },
      { id: 'fall', label: 'Chute > 6m', type: 'checkbox', points: 1 },
      { id: 'blast', label: 'Blast', type: 'checkbox', points: 1 },
      { id: 'crush', label: 'Écrasement', type: 'checkbox', points: 1 },
      { id: 'penetrating', label: 'Traumatisme pénétrant (tête, cou, thorax, abdo)', type: 'checkbox', points: 1 },
      { id: 'unstable_pelvis', label: 'Suspicion fracture bassin instable', type: 'checkbox', points: 1 },
      { id: 'burn', label: 'Brûlure sévère / inhalation fumées', type: 'checkbox', points: 1 },
      { id: 'amputation', label: 'Amputation, paralysie membre', type: 'checkbox', points: 1 },
      { id: 'anticoag', label: 'Trouble hémostase / anticoagulants', type: 'checkbox', points: 1 },
      { id: 'pregnancy', label: 'Grossesse 2e-3e trimestre', type: 'checkbox', points: 1 },
      { id: 'extremes', label: 'Âge > 65 ans ou < 5 ans', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let count = 0;
      Object.values(values).forEach(v => { if (v) count += 1; });
      return { score: count, max: 14 };
    },
    interpret: (score) => {
      if (score === 0) return { level: 'success', text: `Vittel ${score}/14`, detail: 'Aucun critère. Prise en charge standard.' };
      return { level: 'danger', text: `Vittel ${score}/14`, detail: `${score} critère(s) positif(s). Orientation trauma center recommandée.` };
    },
    formula: '≥ 1 critère positif = orientation vers un trauma center'
  },

  // ==================== SCORES W ====================
  wang: {
    name: 'Wang (Bronchiolite)',
    category: 'Pédiatrie',
    icon: Baby,
    description: 'Sévérité bronchiolite',
    color: 'violet',
    fields: [
      { id: 'rr', label: 'Fréquence respiratoire', type: 'select', options: [
        { value: 0, label: '< 40/min' },
        { value: 1, label: '40-50/min' },
        { value: 2, label: '51-60/min' },
        { value: 3, label: '> 60/min' }
      ]},
      { id: 'wheeze', label: 'Sibilants', type: 'select', options: [
        { value: 0, label: 'Absents' },
        { value: 1, label: 'Fin expiratoire' },
        { value: 2, label: 'Tout le cycle expiratoire' },
        { value: 3, label: 'Audibles sans stéthoscope' }
      ]},
      { id: 'retraction', label: 'Tirage', type: 'select', options: [
        { value: 0, label: 'Absent' },
        { value: 1, label: 'Intercostal seul' },
        { value: 2, label: 'Intercostal + sous-costal' },
        { value: 3, label: 'Intercostal + sous-costal + battement ailes nez' }
      ]},
      { id: 'general', label: 'État général', type: 'select', options: [
        { value: 0, label: 'Normal' },
        { value: 3, label: 'Altéré (irritable, difficultés alimentaires)' }
      ]}
    ],
    calculate: (values) => {
      const total = (values.rr || 0) + (values.wheeze || 0) + (values.retraction || 0) + (values.general || 0);
      return { score: total, max: 12 };
    },
    interpret: (score) => {
      if (score <= 3) return { level: 'success', text: `Wang ${score}/12`, detail: 'Bronchiolite légère. Traitement ambulatoire.' };
      if (score <= 8) return { level: 'warning', text: `Wang ${score}/12`, detail: 'Bronchiolite modérée. Surveillance rapprochée. O2 si SpO2 < 94%.' };
      return { level: 'danger', text: `Wang ${score}/12`, detail: 'Bronchiolite sévère. Hospitalisation. Support ventilatoire si besoin.' };
    },
    formula: '0-3: légère. 4-8: modérée. 9-12: sévère'
  },

  westley: {
    name: 'Westley (Laryngite)',
    category: 'ORL',
    icon: Stethoscope,
    description: 'Sévérité laryngite striduleuse / croup',
    color: 'blue',
    fields: [
      { id: 'stridor', label: 'Stridor', type: 'select', options: [
        { value: 0, label: 'Absent' },
        { value: 1, label: 'À l\'agitation' },
        { value: 2, label: 'Au repos' }
      ]},
      { id: 'retraction', label: 'Tirage', type: 'select', options: [
        { value: 0, label: 'Absent' },
        { value: 1, label: 'Léger' },
        { value: 2, label: 'Modéré' },
        { value: 3, label: 'Sévère' }
      ]},
      { id: 'air_entry', label: 'Entrée d\'air', type: 'select', options: [
        { value: 0, label: 'Normale' },
        { value: 1, label: 'Diminuée' },
        { value: 2, label: 'Très diminuée' }
      ]},
      { id: 'cyanosis', label: 'Cyanose', type: 'select', options: [
        { value: 0, label: 'Absente' },
        { value: 4, label: 'À l\'agitation' },
        { value: 5, label: 'Au repos' }
      ]},
      { id: 'consciousness', label: 'Conscience', type: 'select', options: [
        { value: 0, label: 'Normale' },
        { value: 5, label: 'Altérée' }
      ]}
    ],
    calculate: (values) => {
      const total = (values.stridor || 0) + (values.retraction || 0) + (values.air_entry || 0) +
                    (values.cyanosis || 0) + (values.consciousness || 0);
      return { score: total, max: 17 };
    },
    interpret: (score) => {
      if (score <= 2) return { level: 'success', text: `Westley ${score}/17`, detail: 'Croup léger. Corticoïdes PO, retour domicile.' };
      if (score <= 7) return { level: 'warning', text: `Westley ${score}/17`, detail: 'Croup modéré. Dexaméthasone + nébulisation adrénaline.' };
      if (score <= 11) return { level: 'danger', text: `Westley ${score}/17`, detail: 'Croup sévère. Urgence, adrénaline nébulisée, surveillance USC.' };
      return { level: 'danger', text: `Westley ${score}/17`, detail: 'Croup très sévère. Réanimation, intubation à discuter.' };
    },
    formula: '≤ 2: léger. 3-7: modéré. 8-11: sévère. ≥ 12: très sévère'
  },

  wood: {
    name: 'Wood (Asthme pédiatrique)',
    category: 'Pédiatrie',
    icon: Wind,
    description: 'Sévérité crise d\'asthme pédiatrique',
    color: 'blue',
    fields: [
      { id: 'spo2', label: 'SpO2 sous air', type: 'select', options: [
        { value: 0, label: '> 95%' },
        { value: 1, label: '90-95%' },
        { value: 2, label: '< 90%' }
      ]},
      { id: 'wheeze', label: 'Sibilants', type: 'select', options: [
        { value: 0, label: 'Fins expiratoires ou absents' },
        { value: 1, label: 'Expiratoires' },
        { value: 2, label: 'Inspiratoires et expiratoires ou absents (thorax silencieux)' }
      ]},
      { id: 'retraction', label: 'Tirage', type: 'select', options: [
        { value: 0, label: 'Absent' },
        { value: 1, label: 'Modéré' },
        { value: 2, label: 'Sévère + balancement' }
      ]},
      { id: 'speech', label: 'Parole', type: 'select', options: [
        { value: 0, label: 'Phrases complètes' },
        { value: 1, label: 'Mots isolés' },
        { value: 2, label: 'Incapable de parler' }
      ]},
      { id: 'dyspnea', label: 'Dyspnée', type: 'select', options: [
        { value: 0, label: 'Absente au repos' },
        { value: 1, label: 'Présente au repos' },
        { value: 2, label: 'Orthopnée' }
      ]}
    ],
    calculate: (values) => {
      const total = (values.spo2 || 0) + (values.wheeze || 0) + (values.retraction || 0) +
                    (values.speech || 0) + (values.dyspnea || 0);
      return { score: total, max: 10 };
    },
    interpret: (score) => {
      if (score <= 3) return { level: 'success', text: `Wood ${score}/10`, detail: 'Crise légère. β2 inhalés. Retour domicile si amélioration.' };
      if (score <= 5) return { level: 'warning', text: `Wood ${score}/10`, detail: 'Crise modérée. β2 répétés + corticoïdes. Surveillance.' };
      if (score <= 7) return { level: 'danger', text: `Wood ${score}/10`, detail: 'Crise sévère. β2 continus, corticoïdes IV, O2. Surveillance USC.' };
      return { level: 'danger', text: `Wood ${score}/10`, detail: 'Asthme aigu grave. Réanimation, VNI, intubation à discuter.' };
    },
    formula: '0-3: légère. 4-5: modérée. 6-7: sévère. ≥ 8: AAG'
  }
};
